/**
 * 统一的佣金计算服务
 * 适用于前端和后端（Supabase Edge Functions）
 * 
 * 支持从数据库动态读取配置，如果数据库配置不存在则使用默认配置
 */

import { CommissionConfigService } from '../api/commissionConfigService';
import {
  buildZeroPlatformServiceCommission,
  skipsPlatformServiceCommission,
} from '../../utils/companyServiceCommission';

// 默认佣金配置（向后兼容，当数据库配置不存在时使用）
export const DEFAULT_COMMISSION_CONFIG = {
  baseCommissionRate: 0.15,          // 15% 基础佣金率（個人工匠／時薪制）
  /** 已停用：統包服務款不計平台抽成（保留欄位供 DB 相容） */
  companyFixedBaseCommissionRate: 0,
  /** 與 premiumCraftsmanCommissionRate 相同，供舊程式讀取 */
  premiumCommissionRate: 0.10,
  premiumCraftsmanCommissionRate: 0.10,
  /** Premium 統包公司：免平台佣金 */
  premiumCompanyCommissionRate: 0,
  // Premium 方案佣金率（公司統包均為 0%）
  premiumPlanRates: {
    monthly: 0,
    quarterly: 0,
    yearly: 0,
  },
  emergencyFeeMultiplier: 1.5,       // 紧急服务倍数
  matchingFee: 5,                    // 匹配费
  videoConsultationFee: 25,          // 视频咨询费
  warrantyFee: 15,                   // 保修费
  
  // 服务类别费率
  serviceCategoryRates: {
    1: 0.12,   // 水電維修 12%
    2: 0.14,   // 電器維修 14%
    3: 0.13,   // 空調維修 13%
    4: 0.16,   // 一般維修 16%
    5: 0.11,   // 油漆工程 11%
    6: 0.18,   // 清潔服務 18%
    7: 0.10,   // 園藝造景 10%
    8: 0.09,   // 監視系統 9%
    9: 0.12,   // 木工裝修 12%
    10: 0.08,  // 水塔清洗 8%
  } as Record<number, number>,
  
  // 时间因素
  timeBasedRates: {
    peakHours: { start: 18, end: 22, multiplier: 1.2 },
    weekendMultiplier: 1.15,
    holidayMultiplier: 1.25,
  },
  
  // 质量调整（高评分减佣金，低评分加佣金）
  qualityBonusRates: {
    5: -0.02,  // 5星 -2%
    4: -0.01,  // 4星 -1%
    3: 0,      // 3星 0%
    2: 0.01,   // 2星 +1%
    1: 0.02,   // 1星 +2%
  } as Record<number, number>,
  
  // 忠诚度折扣
  loyaltyDiscountRates: {
    10: 0.01,   // 10单 1%折扣
    50: 0.02,   // 50单 2%折扣
    100: 0.03,  // 100单 3%折扣
    200: 0.05,  // 200单 5%折扣
  } as Record<number, number>,
  
  // 批量折扣
  volumeDiscountRates: [
    { threshold: 1000, discountRate: 0.02 },   // 1000+ 2%折扣
    { threshold: 5000, discountRate: 0.05 },   // 5000+ 5%折扣
    { threshold: 10000, discountRate: 0.08 },  // 10000+ 8%折扣
  ],
};

// 配置缓存接口
interface CachedConfig {
  config: typeof DEFAULT_COMMISSION_CONFIG;
  timestamp: number;
  versionNumber?: string;
}

// 配置缓存（内存缓存，5分钟过期）
let configCache: CachedConfig | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5分钟

/**
 * 获取动态配置（优先从数据库读取，失败则使用默认配置）
 */
async function getDynamicConfig(): Promise<typeof DEFAULT_COMMISSION_CONFIG> {
  // 检查缓存
  if (configCache && Date.now() - configCache.timestamp < CACHE_DURATION) {
    return configCache.config;
  }

  try {
    // 尝试从数据库读取配置
    const activeResult = await CommissionConfigService.getActiveConfig();
    if (!activeResult || typeof activeResult !== 'object') {
      throw new Error('Invalid commission config response');
    }
    const { config: dbConfig, error } = activeResult;

    if (!error && dbConfig) {
      // 合并数据库配置和默认配置
      const dynamicConfig = {
        ...DEFAULT_COMMISSION_CONFIG,
        baseCommissionRate: dbConfig.baseCommissionRate,
        companyFixedBaseCommissionRate: dbConfig.companyFixedBaseCommissionRate,
        premiumCommissionRate: dbConfig.premiumCraftsmanCommissionRate,
        premiumCraftsmanCommissionRate: dbConfig.premiumCraftsmanCommissionRate,
        premiumCompanyCommissionRate: dbConfig.premiumCompanyCommissionRate,
        emergencyFeeMultiplier: dbConfig.emergencyFeeMultiplier,
        matchingFee: dbConfig.matchingFee,
        videoConsultationFee: dbConfig.videoConsultationFee,
        warrantyFee: dbConfig.warrantyFee,
        // 如果 advancedConfig 中有服务类别费率，使用它
        serviceCategoryRates: dbConfig.advancedConfig?.serviceCategoryRates 
          ? { ...DEFAULT_COMMISSION_CONFIG.serviceCategoryRates, ...dbConfig.advancedConfig.serviceCategoryRates }
          : DEFAULT_COMMISSION_CONFIG.serviceCategoryRates,
        // 如果 advancedConfig 中有时间费率，使用它
        timeBasedRates: dbConfig.advancedConfig?.timeBasedRates
          ? { ...DEFAULT_COMMISSION_CONFIG.timeBasedRates, ...dbConfig.advancedConfig.timeBasedRates }
          : DEFAULT_COMMISSION_CONFIG.timeBasedRates,
        // 如果 advancedConfig 中有质量奖励，使用它
        qualityBonusRates: dbConfig.advancedConfig?.qualityBonusRates
          ? { ...DEFAULT_COMMISSION_CONFIG.qualityBonusRates, ...dbConfig.advancedConfig.qualityBonusRates }
          : DEFAULT_COMMISSION_CONFIG.qualityBonusRates,
        // 如果 advancedConfig 中有忠诚度折扣，使用它
        loyaltyDiscountRates: dbConfig.advancedConfig?.loyaltyDiscountRates
          ? { ...DEFAULT_COMMISSION_CONFIG.loyaltyDiscountRates, ...dbConfig.advancedConfig.loyaltyDiscountRates }
          : DEFAULT_COMMISSION_CONFIG.loyaltyDiscountRates,
        // 如果 advancedConfig 中有批量折扣，使用它
        volumeDiscountRates: dbConfig.advancedConfig?.volumeDiscountRates
          ? dbConfig.advancedConfig.volumeDiscountRates
          : DEFAULT_COMMISSION_CONFIG.volumeDiscountRates,
      };

      // 更新缓存
      configCache = {
        config: dynamicConfig,
        timestamp: Date.now(),
        versionNumber: dbConfig.versionNumber,
      };

      return dynamicConfig;
    }
  } catch (error) {
    console.warn('Failed to load commission config from database, using defaults:', error);
  }

  // 使用默认配置
  if (!configCache) {
    configCache = {
      config: DEFAULT_COMMISSION_CONFIG,
      timestamp: Date.now(),
    };
  }

  return configCache.config;
}

/**
 * 同步获取配置（用于同步计算，优先使用缓存）
 */
function getConfigSync(): typeof DEFAULT_COMMISSION_CONFIG {
  if (configCache && Date.now() - configCache.timestamp < CACHE_DURATION) {
    return configCache.config;
  }
  // 如果缓存过期或不存在，返回默认配置（异步刷新会在后台进行）
  return DEFAULT_COMMISSION_CONFIG;
}

// 向后兼容：导出 COMMISSION_CONFIG 作为默认配置的别名
export const COMMISSION_CONFIG = DEFAULT_COMMISSION_CONFIG;

// 佣金计算参数接口
export interface CommissionParams {
  serviceFee: number;              // 服务费
  isPremium: boolean;              // 是否高级工匠/公司
  premiumPlanType?: 'monthly' | 'quarterly' | 'yearly'; // Premium 方案类型
  isEmergency?: boolean;           // 是否紧急
  categoryId?: number;             // 服务类别ID
  craftsmanRating?: number;        // 工匠评分
  completedJobs?: number;          // 已完成订单数
  serviceDate?: Date;              // 服务日期
  isPeakHours?: boolean;           // 是否高峰时段
  isWeekend?: boolean;             // 是否周末
  isHoliday?: boolean;             // 是否假日
  isVideoConsultation?: boolean;   // 是否视频咨询
  hasWarranty?: boolean;           // 是否保修
  /** 統包案件（pricing_mode = company_fixed）；非 Premium 時使用 companyFixedBaseCommissionRate，不套用服務類別覆寫 */
  isCompanyFixed?: boolean;
}

// 佣金计算结果接口
export interface CommissionResult {
  serviceFee: number;              // 原始服务费
  baseAmount: number;              // 基础金额（含时间调整）
  commission: number;              // 平台佣金
  matchingFee: number;             // 匹配费
  platformFee: number;             // 平台总费用
  craftsmanEarnings: number;       // 工匠收入
  totalCustomerCharge: number;     // 客户总费用
  breakdown: {                     // 费用明细
    baseCommission: number;
    categoryAdjustment: number;
    timeAdjustment: number;
    qualityBonus: number;
    loyaltyDiscount: number;
    emergencyFee: number;
    videoConsultationFee: number;
    warrantyFee: number;
  };
  commissionRate: number;          // 实际佣金率
  isPremium: boolean;
  isEmergency: boolean;
}

/**
 * 统一佣金计算服务类
 */
export class CommissionService {
  /**
   * 刷新配置缓存（供管理员页面调用）
   */
  static async refreshConfig(): Promise<void> {
    configCache = null;
    await getDynamicConfig(); // 预加载新配置
  }

  /**
   * 获取当前使用的配置版本号
   */
  static getConfigVersion(): string | undefined {
    return configCache?.versionNumber;
  }

  /**
   * 计算佣金（完整版本）
   * 支持同步和异步两种模式
   */
  static calculate(params: CommissionParams): CommissionResult;
  static calculate(params: CommissionParams, useAsyncConfig: true): Promise<CommissionResult>;
  static calculate(params: CommissionParams, useAsyncConfig?: boolean): CommissionResult | Promise<CommissionResult> {
    if (useAsyncConfig) {
      return this.calculateAsync(params);
    }
    return this.calculateSync(params);
  }

  /**
   * 同步计算（使用缓存或默认配置）
   */
  private static calculateSync(params: CommissionParams): CommissionResult {
    const config = getConfigSync();
    return this.calculateWithConfig(params, config);
  }

  /**
   * 异步计算（从数据库读取最新配置）
   */
  private static async calculateAsync(params: CommissionParams): Promise<CommissionResult> {
    const config = await getDynamicConfig();
    return this.calculateWithConfig(params, config);
  }

  /**
   * 使用指定配置进行计算
   */
  private static calculateWithConfig(
    params: CommissionParams,
    config: typeof DEFAULT_COMMISSION_CONFIG
  ): CommissionResult {
    const {
      serviceFee,
      isPremium,
      premiumPlanType,
      isEmergency = false,
      craftsmanRating = 5,
      completedJobs = 0,
      isPeakHours = false,
      isWeekend = false,
      isHoliday = false,
      isVideoConsultation = false,
      hasWarranty = false,
      isCompanyFixed = false,
    } = params;

    // 1. 基础金额计算
    let baseAmount = serviceFee;

    // 2. 紧急服务调整
    if (isEmergency) {
      baseAmount *= config.emergencyFeeMultiplier;
    }

    // 3. 时间因素调整
    let timeMultiplier = 1;
    if (isPeakHours) timeMultiplier *= config.timeBasedRates.peakHours.multiplier;
    if (isWeekend) timeMultiplier *= config.timeBasedRates.weekendMultiplier;
    if (isHoliday) timeMultiplier *= config.timeBasedRates.holidayMultiplier;
    baseAmount *= timeMultiplier;
    baseAmount = Math.round(baseAmount * 100) / 100;

    const videoConsultationFee = isVideoConsultation ? config.videoConsultationFee : 0;
    const warrantyFee = hasWarranty ? config.warrantyFee : 0;

    // 統包（客戶付公司）：平台不抽服務款佣金
    if (skipsPlatformServiceCommission(isCompanyFixed)) {
      const zero = buildZeroPlatformServiceCommission({
        serviceFee,
        baseAmount,
        isPremium,
        isEmergency,
        videoConsultationFee,
        warrantyFee,
      });
      return {
        ...zero,
        breakdown: {
          baseCommission: 0,
          categoryAdjustment: 0,
          timeAdjustment: 0,
          qualityBonus: 0,
          loyaltyDiscount: 0,
          emergencyFee: isEmergency
            ? Math.round((baseAmount - serviceFee) * 100) / 100
            : 0,
          videoConsultationFee,
          warrantyFee,
        },
      };
    }

    // 4. 确定基础佣金率（Premium 走方案／優質率；其餘走 baseCommissionRate）
    let categoryCommissionRate = config.baseCommissionRate;
    if (!isPremium && isCompanyFixed) {
      categoryCommissionRate = config.companyFixedBaseCommissionRate;
    }

    // Premium 佣金率：方案類型優先，否則依統包／個人工匠預設
    const premiumDefault = isCompanyFixed
      ? config.premiumCompanyCommissionRate
      : config.premiumCraftsmanCommissionRate;
    let premiumCommissionRate = premiumDefault;
    if (isPremium && premiumPlanType && config.premiumPlanRates[premiumPlanType]) {
      premiumCommissionRate = config.premiumPlanRates[premiumPlanType];
    }

    const baseCommissionRate = isPremium 
      ? premiumCommissionRate 
      : categoryCommissionRate;

    // 5–8. 統包（company_fixed）僅用約定佣金率，不套用星級、忠誠度調整；
    // 時薪制保留星級與忠誠度調整。級距折扣（volumeDiscount）已停用。
    const qualityAdjustment = isCompanyFixed
      ? 0
      : (config.qualityBonusRates[craftsmanRating] || 0);
    let adjustedCommissionRate = baseCommissionRate + qualityAdjustment;

    let loyaltyDiscount = 0;
    if (!isCompanyFixed) {
      for (const [threshold, discount] of Object.entries(config.loyaltyDiscountRates)) {
        if (completedJobs >= parseInt(threshold)) {
          loyaltyDiscount = Math.max(loyaltyDiscount, discount);
        }
      }
    }

    // 需求調整：停用級距折扣，避免 1000+/5000+ 等門檻自動降低佣金率
    const volumeDiscount = 0;

    const finalCommissionRate = adjustedCommissionRate - loyaltyDiscount - volumeDiscount;

    // 9. 费用计算
    const baseCommission = baseAmount * baseCommissionRate;
    const categoryAdjustment = baseAmount * (categoryCommissionRate - config.baseCommissionRate);
    const timeAdjustment = baseAmount * (timeMultiplier - 1);
    const qualityBonus = baseAmount * qualityAdjustment;
    const loyaltyDiscountAmount = baseAmount * loyaltyDiscount;
    const emergencyFee = isEmergency ? baseAmount * (config.emergencyFeeMultiplier - 1) : 0;

    // 10. 最终计算
    const commission = Math.round(baseAmount * finalCommissionRate * 100) / 100;
    const matchingFee = config.matchingFee;
    const platformFee = commission + matchingFee + videoConsultationFee + warrantyFee;
    const craftsmanEarnings = Math.round((baseAmount - platformFee) * 100) / 100;
    const totalCustomerCharge = baseAmount + videoConsultationFee + warrantyFee;

    return {
      serviceFee,
      baseAmount: Math.round(baseAmount * 100) / 100,
      commission,
      matchingFee,
      platformFee: Math.round(platformFee * 100) / 100,
      craftsmanEarnings,
      totalCustomerCharge: Math.round(totalCustomerCharge * 100) / 100,
      breakdown: {
        baseCommission: Math.round(baseCommission * 100) / 100,
        categoryAdjustment: Math.round(categoryAdjustment * 100) / 100,
        timeAdjustment: Math.round(timeAdjustment * 100) / 100,
        qualityBonus: Math.round(qualityBonus * 100) / 100,
        loyaltyDiscount: Math.round(loyaltyDiscountAmount * 100) / 100,
        emergencyFee: Math.round(emergencyFee * 100) / 100,
        videoConsultationFee,
        warrantyFee,
      },
      commissionRate: Math.round(finalCommissionRate * 10000) / 100,
      isPremium,
      isEmergency,
    };
  }

  /**
   * 简化版本：只计算基础佣金（向后兼容）
   */
  static calculateBasic(
    serviceFee: number,
    isPremium: boolean,
    isEmergency: boolean = false
  ): number {
    const result = this.calculateSync({
      serviceFee,
      isPremium,
      isEmergency,
    });
    return result.commission;
  }

  /**
   * 计算工匠实际收入（服务费 - 佣金）
   */
  static calculateCraftsmanEarnings(serviceFee: number, commission: number): number {
    return Math.round((serviceFee - commission) * 100) / 100;
  }

  /**
   * 获取佣金详情（向后兼容旧接口）
   * 支援方案類型參數
   */
  static getCommissionDetails(
    serviceFee: number,
    craftsman: {
      isPremium: boolean;
      premiumPlanType?: 'monthly' | 'quarterly' | 'yearly';
      isCompanyFixed?: boolean;
    },
    isEmergency: boolean = false
  ) {
    const config = getConfigSync();
    const result = this.calculateSync({
      serviceFee,
      isPremium: craftsman.isPremium,
      premiumPlanType: craftsman.premiumPlanType,
      isEmergency,
      isCompanyFixed: craftsman.isCompanyFixed,
    });
    
    const displayCommissionRate = craftsman.isCompanyFixed
      ? 0
      : craftsman.isPremium
        ? craftsman.premiumPlanType &&
          config.premiumPlanRates[craftsman.premiumPlanType]
          ? config.premiumPlanRates[craftsman.premiumPlanType]
          : config.premiumCraftsmanCommissionRate
        : config.baseCommissionRate;
    
    return {
      serviceFee: result.serviceFee,
      commission: result.commission,
      craftsmanEarnings: result.craftsmanEarnings,
      commissionRate: displayCommissionRate,
      matchingFee: result.matchingFee,
      platformFee: result.platformFee,
      isPremium: craftsman.isPremium,
      isEmergency,
      emergencyMultiplier: isEmergency ? config.emergencyFeeMultiplier : 1,
    };
  }

  /**
   * 根據方案類型獲取 Premium 佣金率
   */
  static getPremiumCommissionRate(planType?: 'monthly' | 'quarterly' | 'yearly'): number {
    const config = getConfigSync();
    if (planType && config.premiumPlanRates[planType]) {
      return config.premiumPlanRates[planType];
    }
    return config.premiumCraftsmanCommissionRate;
  }
}

// 初始化：在应用启动时预加载配置（可选）
if (typeof window !== 'undefined') {
  // 前端环境：异步预加载配置
  getDynamicConfig().catch((error) => {
    console.warn('Failed to preload commission config:', error);
  });
}

